import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BackendService, TypeOrmFindManyOptions } from '@wilson/base';
import { ConfigOptions, ConfigService } from '@wilson/config';
import {
  CreatePayrollSetting,
  CreatePayrollSettingCategory,
  PayrollSetting,
  PayrollSettingCategory,
  UserPayrollSetting,
} from '@wilson/interfaces';
import { TransformMasterDataTranslationService } from '@wilson/non-domain-specific/master-data-translate';
import { stringify } from 'qs';
import { firstValueFrom, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PayrollSettingsService extends BackendService<PayrollSetting> {
  protected readonly http = inject(HttpClient);
  protected readonly config = inject<ConfigOptions>(ConfigService);
  protected readonly transformMasterDataTranslationService = inject(
    TransformMasterDataTranslationService,
  );

  protected readonly path = 'payroll-settings';

  public getPayrollSettingAssignedUsers(
    id: string,
  ): Observable<UserPayrollSetting[]> {
    return this.http.get<UserPayrollSetting[]>(
      `${this.config.host}/${this.path}/${id}/active-users`,
    );
  }

  public getActiveUserPayrollSetting(
    userId: string,
    date: string,
  ): Observable<UserPayrollSetting> {
    return this.http.get<UserPayrollSetting>(
      `${this.config.host}/user-payroll-settings/${userId}/active/${date}`,
    );
  }
  public bulkDeleteUsersAssignedPayrollSetting(userIds: string[]) {
    return this.http.patch(`${this.config.host}/user-payroll-settings/delete`, {
      ids: userIds,
    });
  }

  public createUserPayrollSetting(
    payload: CreatePayrollSetting[],
  ): Observable<PayrollSetting> {
    return this.http.post<PayrollSetting>(
      `${this.config.host}/user-payroll-settings`,
      {
        items: payload,
      },
    );
  }

  public getPayrollSetting(
    payrollSettingId: string,
    options?: TypeOrmFindManyOptions,
  ): Observable<PayrollSetting> {
    const params = stringify(options);
    return this.http
      .get<PayrollSetting>(
        `${this.config.host}/${this.path}/${payrollSettingId}?${params}`,
      )
      .pipe(
        map((payrollSetting) => {
          payrollSetting.payrollSettingCategories.map(
            (payrollSettingCategory) => {
              if (payrollSettingCategory.payrollCategory) {
                payrollSettingCategory.payrollCategory =
                  this.transformMasterDataTranslationService.transform([
                    payrollSettingCategory.payrollCategory,
                  ])[0];
              }
              return payrollSettingCategory;
            },
          );
          return payrollSetting;
        }),
      );
  }

  public createPayrollSettingCategory(
    payload: CreatePayrollSettingCategory,
  ): Observable<PayrollSettingCategory> {
    return this.http.post<PayrollSettingCategory>(
      `${this.config.host}/payroll-setting-categories`,
      payload,
    );
  }

  public updatePayrollSettingCategory(
    id: string,
    payload: Partial<PayrollSettingCategory>,
  ): Observable<PayrollSettingCategory> {
    return this.http.patch<PayrollSettingCategory>(
      `${this.config.host}/payroll-setting-categories/${id}`,
      payload,
    );
  }

  public deleteUserPayrollSetting(
    entityId: string,
  ): Promise<PayrollSettingCategory[]> {
    return firstValueFrom(
      this.http.delete<PayrollSettingCategory[]>(
        `${this.config.host}/payroll-setting-categories/${entityId}`,
      ),
    );
  }

  public getActiveUserPayrollSettingForUsers(
    userIds: string[],
  ): Observable<UserPayrollSetting[]> {
    return this.http.post<UserPayrollSetting[]>(
      `${this.config.host}/user-payroll-settings/users/active`,
      {
        userIds,
      },
    );
  }
}
